import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EMPTY_ENTRY, validateEntry } from '../../lib/bibliography.js';
import { fetchSingleEntry } from '../../lib/crossref.js';
import { findSimilarBibliography } from '../../lib/findSimilar.js';
import { useSimilarCheck } from '../../hooks/useSimilarCheck.js';
import SimilarEntriesWarning from '../common/SimilarEntriesWarning.jsx';
import AnnounceCheckbox from '../common/AnnounceCheckbox.jsx';
import { SUPPORTED_LANGUAGES } from '../../i18n.js';

// Languages we commonly accept on submission. Mirrors what's in the dataset.
const FORM_LANGUAGES = [
  ...SUPPORTED_LANGUAGES,
  { code: 'gl', name: 'Galego', flag: '' },
  { code: 'hu', name: 'Magyar', flag: '' },
  { code: 'pl', name: 'Polski', flag: '' },
];

const TYPE_FIELDS = {
  book: ['publisher', 'place', 'edition'],
  article: ['journal', 'volume', 'issue', 'pages'],
  chapter: ['booktitle', 'editors', 'publisher', 'place', 'pages'],
  conference: ['conference', 'editors', 'place', 'pages'],
};

// Reusable add/edit form.
//
// Props:
//   initialValue: optional starting state (for edit). Falls back to EMPTY_ENTRY.
//   submitLabel:  text on the submit button.
//   onSubmit:     async (formState) => void  — caller does the actual mutation.
//   onCancel:     () => void.
//   showReason:   if true, exposes a 'reason' textarea (used by suggest-edit).
//                 onSubmit receives (form, reasonText).
//   showAnnounce: if true, exposes the "Include in What's New" checkbox
//                 (add form only; stored as announce_in_whats_new).
//   busy:         disables the submit button while saving.
export default function EntryForm({
  initialValue,
  submitLabel,
  onSubmit,
  onCancel,
  showReason = false,
  enableAutofill = false,
  showAnnounce = false,
  busy = false,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => ({ ...EMPTY_ENTRY, ...initialValue }));
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [autofillOpen, setAutofillOpen] = useState(false);
  const [autofillInput, setAutofillInput] = useState('');
  const [autofillBusy, setAutofillBusy] = useState(false);
  const [autofillError, setAutofillError] = useState(null);

  // Raw textarea/input strings for the three array-typed fields. We can't
  // derive them from form.authors / form.editors / form.keywords on every
  // render, because that would re-join on every keystroke — and our parsing
  // step trims each segment, so any in-progress trailing space ("Smith, Joe ")
  // would be stripped immediately and the user couldn't type a new word.
  // We only parse these strings into arrays on submit.
  const [authorsText, setAuthorsText] = useState(() => (initialValue?.authors ?? []).join('\n'));
  const [editorsText, setEditorsText] = useState(() => (initialValue?.editors ?? []).join('\n'));
  const [keywordsText, setKeywordsText] = useState(() => (initialValue?.keywords ?? []).join(', '));

  // Reset only when the edited entry actually changes (different id), not
  // on every new prop reference — anything that caused BibliographyEdit to
  // refetch (notably supabase-js's SIGNED_IN event on tab refocus) would
  // otherwise wipe the user's in-progress edits.
  useEffect(() => {
    if (initialValue) {
      setForm({ ...EMPTY_ENTRY, ...initialValue });
      setAuthorsText((initialValue.authors ?? []).join('\n'));
      setEditorsText((initialValue.editors ?? []).join('\n'));
      setKeywordsText((initialValue.keywords ?? []).join(', '));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue?.id]);

  function update(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
    setError(null);
  }

  // Parse the loose-text fields into clean arrays the way validateEntry +
  // the DB expect them.
  function parseListText(text, separator) {
    return text.split(separator).map((s) => s.trim()).filter(Boolean);
  }

  // Duplicate detection — debounced query against the published table
  // looking for entries with a similar title and (optionally) the same
  // year. Renders as an informational warning panel above the submit
  // button; never blocks submission.
  const { candidates: similarCandidates } = useSimilarCheck(
    { title: form.title, year: form.year, excludeId: initialValue?.id },
    findSimilarBibliography,
  );

  // Pull metadata for a single DOI from Crossref and drop it into the form.
  // Overwrites typed fields (the user asked for it) but preserves any keywords
  // they've already entered, since Crossref rarely supplies them.
  async function handleAutofill() {
    const value = autofillInput.trim();
    if (!value) {
      setAutofillError(t('bibliography.form.autofill.errors.noDoi'));
      return;
    }
    setAutofillError(null);
    setAutofillBusy(true);
    try {
      const entry = await fetchSingleEntry(value);
      setForm((prev) => ({ ...prev, ...entry, keywords: prev.keywords }));
      setAuthorsText((entry.authors ?? []).join('\n'));
      if (entry.editors?.length) setEditorsText(entry.editors.join('\n'));
      setError(null);
      setAutofillOpen(false);
      setAutofillInput('');
    } catch (err) {
      const keys = {
        NO_DOI: 'bibliography.form.autofill.errors.noDoi',
        NOT_FOUND: 'bibliography.form.autofill.errors.notFound',
        FETCH_FAILED: 'bibliography.form.autofill.errors.failed',
      };
      setAutofillError(t(keys[err.message] ?? 'bibliography.form.autofill.errors.failed'));
    } finally {
      setAutofillBusy(false);
    }
  }

  function setLanguage(code) {
    const match = FORM_LANGUAGES.find((l) => l.code === code);
    update({
      language: code || '',
      language_display: match?.name ?? '',
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    // Parse the array-typed fields exactly once, on submit, so the user's
    // typing flow isn't disrupted by trim/filter on every keystroke.
    const submitForm = {
      ...form,
      authors: parseListText(authorsText, /\n/),
      editors: parseListText(editorsText, /\n/),
      keywords: parseListText(keywordsText, ','),
    };
    const errKey = validateEntry(submitForm);
    if (errKey) {
      setError(t(errKey));
      return;
    }
    try {
      await onSubmit(submitForm, reason);
    } catch (err) {
      setError(err.message ?? String(err));
    }
  }

  const visibleTypeFields = TYPE_FIELDS[form.type] ?? [];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      {/* Optional: autofill the whole form from a single DOI via Crossref.
          Collapsed behind a button so it reads as an option, not a required step. */}
      {enableAutofill &&
        (autofillOpen ? (
          <div className="rounded-md border border-white/10 bg-navy-deep/40 p-4">
            <div className="mb-1 flex items-center justify-between">
              <span className="block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
                {t('bibliography.form.autofill.label')}
              </span>
              <button
                type="button"
                onClick={() => {
                  setAutofillOpen(false);
                  setAutofillError(null);
                }}
                className="text-lg leading-none text-ink-muted hover:text-ink-secondary"
                aria-label={t('common.cancel')}
              >
                ×
              </button>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                autoFocus
                value={autofillInput}
                onChange={(e) => {
                  setAutofillInput(e.target.value);
                  setAutofillError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAutofill();
                  }
                }}
                placeholder={t('bibliography.form.autofill.placeholder')}
                className="form-input flex-1"
              />
              <button
                type="button"
                className="btn-primary whitespace-nowrap"
                onClick={handleAutofill}
                disabled={autofillBusy || !autofillInput.trim()}
              >
                {autofillBusy ? t('bibliography.import.loading') : t('bibliography.form.autofill.button')}
              </button>
            </div>
            <span className="mt-1 block text-xs text-ink-muted">{t('bibliography.form.autofill.hint')}</span>
            {autofillError && <span className="mt-1 block text-xs text-red-300">{autofillError}</span>}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAutofillOpen(true)}
            className="self-start rounded-md border border-dashed border-white/15 px-3 py-1.5 text-xs text-ink-secondary transition hover:border-gold/40 hover:text-gold-light"
          >
            {t('bibliography.form.autofill.toggle')}
          </button>
        ))}

      {/* Type */}
      <Field label={t('bibliography.form.type')} required>
        <div className="flex flex-wrap gap-2">
          {Object.keys(TYPE_FIELDS).map((type) => (
            <label
              key={type}
              className={`cursor-pointer rounded-full border px-4 py-1.5 text-sm transition ${
                form.type === type
                  ? 'border-gold/60 bg-navy-mid/60 text-gold-light'
                  : 'border-white/10 bg-navy-deep/40 text-ink-secondary hover:border-gold/30'
              }`}
            >
              <input
                type="radio"
                name="entry-type"
                value={type}
                checked={form.type === type}
                onChange={(e) => update({ type: e.target.value })}
                className="sr-only"
              />
              {t(`bibliography.types.${type}`)}
            </label>
          ))}
        </div>
      </Field>

      {/* Core fields */}
      <Field label={t('bibliography.form.title')} required>
        <Input
          value={form.title}
          onChange={(v) => update({ title: v })}
          placeholder={t('bibliography.form.titlePlaceholder')}
        />
      </Field>

      <Field
        label={t('bibliography.form.authors')}
        hint={t('bibliography.form.authorsHint')}
        required
      >
        <Textarea
          rows={3}
          value={authorsText}
          onChange={(v) => { setAuthorsText(v); setError(null); }}
          placeholder={t('bibliography.form.authorsPlaceholder')}
        />
      </Field>

      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('bibliography.form.year')}>
          <Input
            type="number"
            inputMode="numeric"
            value={form.year ?? ''}
            onChange={(v) => update({ year: v === '' ? null : v })}
            placeholder="2024"
          />
        </Field>
        <Field label={t('bibliography.form.language')}>
          <select
            value={form.language ?? ''}
            onChange={(e) => setLanguage(e.target.value)}
            className="form-input"
          >
            <option value="">{t('bibliography.form.languageNone')}</option>
            {FORM_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {/* Type-specific fields */}
      {visibleTypeFields.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2">
          {visibleTypeFields.map((key) => {
            if (key === 'editors') {
              return (
                <div className="md:col-span-2" key={key}>
                  <Field
                    label={t(`bibliography.detail.${key}`)}
                    hint={t('bibliography.form.authorsHint')}
                  >
                    <Textarea
                      rows={2}
                      value={editorsText}
                      onChange={(v) => { setEditorsText(v); setError(null); }}
                    />
                  </Field>
                </div>
              );
            }
            return (
              <Field key={key} label={t(`bibliography.detail.${key}`)}>
                <Input
                  value={form[key] ?? ''}
                  onChange={(v) => update({ [key]: v })}
                />
              </Field>
            );
          })}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('bibliography.detail.doi')}>
          <Input
            value={form.doi ?? ''}
            onChange={(v) => update({ doi: v })}
            placeholder="10.xxxx/yyy"
          />
        </Field>
        <Field label={t('bibliography.detail.url')}>
          <Input
            type="text"
            inputMode="url"
            value={form.url ?? ''}
            onChange={(v) => update({ url: v })}
            placeholder="https://…"
          />
        </Field>
      </div>

      <Field
        label={t('bibliography.form.keywords')}
        hint={t('bibliography.form.keywordsHint')}
      >
        <Input
          value={keywordsText}
          onChange={(v) => { setKeywordsText(v); setError(null); }}
          placeholder={t('bibliography.form.keywordsPlaceholder')}
        />
        {/* Live preview of how the comma-separated input will be parsed
            into chips on save — same trim+filter we apply on submit. */}
        {(() => {
          const preview = parseListText(keywordsText, ',');
          if (preview.length === 0) return null;
          return (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {preview.map((k) => (
                <span
                  key={k}
                  className="rounded-full border border-white/10 bg-navy-mid/40 px-2 py-0.5 text-xs text-ink-secondary"
                >
                  {k}
                </span>
              ))}
            </div>
          );
        })()}
      </Field>

      <Field label={t('bibliography.detail.abstract')}>
        <Textarea
          rows={6}
          value={form.abstract ?? ''}
          onChange={(v) => update({ abstract: v })}
        />
      </Field>

      {showReason && (
        <Field
          label={t('bibliography.form.reason')}
          hint={t('bibliography.form.reasonHint')}
        >
          <Textarea rows={3} value={reason} onChange={setReason} />
        </Field>
      )}

      {showAnnounce && (
        <AnnounceCheckbox
          checked={form.announce_in_whats_new}
          onChange={(v) => update({ announce_in_whats_new: v })}
        />
      )}

      <SimilarEntriesWarning candidates={similarCandidates} kind="bibliography" />

      {error && (
        <p className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? t('common.loading') : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
        {label}
        {required && <span className="ml-1 text-gold">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
    </label>
  );
}

function Input({ value, onChange, ...rest }) {
  return (
    <input
      {...rest}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="form-input"
    />
  );
}

function Textarea({ value, onChange, rows = 4, ...rest }) {
  return (
    <textarea
      {...rest}
      rows={rows}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="form-input"
    />
  );
}
